import React from 'react';
import { DeckCardList } from './DeckCardList';
import { PokemonCard, DeckCard } from '../../types/pokemon';
import { Loader2 } from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';

interface DeckPreviewPanelProps {
  cards: { card_id: number; quantity: number; card: PokemonCard }[];
  totalCards: number;
  onSubmit: () => void;
  onCancel?: () => void;
  submitLabel: string;
  isSubmitting: boolean;
  showCancelButton?: boolean;
  hasChanges?: boolean;
  onRemoveCard?: (cardId: number) => void;
}

export function DeckPreviewPanel({
  cards,
  totalCards,
  onSubmit,
  onCancel,
  submitLabel,
  isSubmitting,
  showCancelButton = false,
  hasChanges = true,
  onRemoveCard
}: DeckPreviewPanelProps) {
  const { t } = useI18n();
  
  // Convertir les cartes au format DeckCard
  const formattedCards: DeckCard[] = cards.map(card => ({
    id: `temp-${card.card_id}`, // ID temporaire car non utilisé dans l'affichage
    deck_id: 'temp', // ID temporaire car non utilisé dans l'affichage
    card_id: card.card_id,
    quantity: card.quantity,
    card: card.card
  }));

  return (
    <div className="sticky top-8">
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-gray-900">{t('create_deck.preview.title')}</h2>
          <span className={`text-sm font-medium ${totalCards === 20 ? 'text-green-600' : 'text-red-500'}`}>
            {totalCards}/20 {t('create_deck.preview.cards')}
          </span>
        </div>
        <div className="max-h-[500px] overflow-y-auto mb-6">
          <DeckCardList 
            cards={formattedCards} 
            onRemoveCard={onRemoveCard}
            showRemoveButton={!!onRemoveCard}
          />
        </div>

        <div className="mt-6 flex gap-3">
          {showCancelButton && onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              {t('common.cancel')}
            </button>
          )}
          <button
            type="submit"
            onClick={onSubmit}
            disabled={isSubmitting || !hasChanges || totalCards !== 20}
            className="flex-1 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t('common.loading')}
              </>
            ) : (
              submitLabel
            )}
          </button>
        </div>
      </div>
    </div>
  );
}